import { useEffect, useRef, useState } from "react";
import type { TranscriptEntry } from "../hooks/useGladiaTranscription";
import {
  loadTranscriptHistory,
  type PersistedTranscriptEntry,
} from "../services/transcripts";
import {
  buildTranscriptExport,
  buildTranscriptFilename,
} from "./transcriptExport";

const LANGUAGES: { code: string; label: string }[] = [
  { code: "automatic", label: "Auto-detect" },
  { code: "fr", label: "Fran\u00e7ais" },
  { code: "en", label: "English" },
  { code: "es", label: "Espa\u00f1ol" },
  { code: "de", label: "Deutsch" },
  { code: "it", label: "Italiano" },
  { code: "pt", label: "Portugu\u00eas" },
  { code: "nl", label: "Nederlands" },
  { code: "ja", label: "\u65e5\u672c\u8a9e" },
  { code: "zh", label: "\u4e2d\u6587" },
  { code: "ko", label: "\ud55c\uad6d\uc5b4" },
  { code: "ar", label: "\u0627\u0644\u0639\u0631\u0628\u064a\u0629" },
  { code: "ru", label: "\u0420\u0443\u0441\u0441\u043a\u0438\u0439" },
];

interface TranscriptPanelProps {
  sessionId: string;
  transcripts: TranscriptEntry[];
  currentPartial: string;
  isListening: boolean;
  error: string | null;
  enabled: boolean;
  onStart: () => void;
  onStop: () => void;
  debugInfo?: string;
  language: string;
  onLanguageChange: (lang: string) => void;
}

/**
 * Panel that displays real-time transcription from Gladia.
 * Shows final transcript entries and the current partial (in-progress) text.
 */
export default function TranscriptPanel({
  sessionId,
  transcripts,
  currentPartial,
  isListening,
  error,
  enabled,
  onStart,
  onStop,
  debugInfo,
  language,
  onLanguageChange,
}: TranscriptPanelProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [exportStatus, setExportStatus] = useState<string | null>(null);
  const hasFinalTranscript = transcripts.some(
    (entry) => entry.isFinal && entry.text.trim().length > 0,
  );

  // Auto-scroll to bottom when new transcripts arrive
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [transcripts, currentPartial]);

  const downloadTranscript = async () => {
    setIsExporting(true);
    setExportStatus(null);
    let historyUnavailable = false;
    try {
      let history: PersistedTranscriptEntry[] = [];
      try {
        history = await loadTranscriptHistory(sessionId);
      } catch {
        historyUnavailable = true;
      }

      const content = buildTranscriptExport(history, transcripts);
      if (!content) {
        setExportStatus(
          historyUnavailable
            ? "Transcript history unavailable and no finalized local transcript to download."
            : "No finalized transcript to download.",
        );
        return;
      }

      const objectUrl = URL.createObjectURL(
        new Blob([content], { type: "text/plain;charset=utf-8" }),
      );
      try {
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = buildTranscriptFilename(sessionId);
        link.click();
      } finally {
        URL.revokeObjectURL(objectUrl);
      }
      setExportStatus(
        historyUnavailable
          ? "Transcript history unavailable — downloaded the current transcript."
          : "Transcript downloaded.",
      );
    } catch {
      setExportStatus("Transcript download failed.");
    } finally {
      setIsExporting(false);
    }
  };

  if (!enabled) {
    return (
      <div
        style={{
          borderLeft: "1px solid #333",
          background: "#0a0a0a",
          padding: "8px 12px",
          fontSize: 11,
          color: "#666",
          width: 320,
          flexShrink: 0,
        }}
      >
        Transcription disabled — the server has no Gladia key (BACKEND_GLADIA_API_KEY)
      </div>
    );
  }

  return (
    <div
      style={{
        borderLeft: "1px solid #333",
        background: "#0d0d1a",
        width: 340,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px 12px",
          borderBottom: "1px solid #1a1a2e",
          flexShrink: 0,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: isListening ? "#22c55e" : "#666",
              display: "inline-block",
              animation: isListening ? "pulse 1.5s infinite" : "none",
            }}
          />
          <span style={{ fontSize: 12, fontWeight: 600, color: "#a78bfa" }}>
            Transcript
          </span>
          {isListening && (
            <span style={{ fontSize: 10, color: "#666" }}>Live</span>
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <button
            type="button"
            onClick={downloadTranscript}
            disabled={isExporting || !hasFinalTranscript}
            aria-label="Download transcript from the beginning"
            title="Download transcript from the beginning"
            style={{
              background: "#1e293b",
              color: "#cbd5e1",
              border: "1px solid #334155",
              borderRadius: 4,
              padding: "3px 8px",
              fontSize: 11,
              cursor:
                isExporting || !hasFinalTranscript ? "not-allowed" : "pointer",
              opacity: isExporting || !hasFinalTranscript ? 0.5 : 1,
            }}
          >
            <span aria-hidden="true">⇩</span>{" "}
            {isExporting ? "Preparing…" : "Download"}
          </button>
          <button
            type="button"
            onClick={isListening ? onStop : onStart}
            style={{
              background: isListening ? "#991b1b" : "#4c1d95",
              color: isListening ? "#fca5a5" : "#c4b5fd",
              border: "none",
              borderRadius: 4,
              padding: "3px 10px",
              fontSize: 11,
              cursor: "pointer",
            }}
          >
            {isListening ? "Stop" : "Start"}
          </button>
        </div>
      </div>

      {/* Language selector — inline buttons */}
      <div
        style={{
          padding: "6px 12px",
          borderBottom: "1px solid #1a1a2e",
          flexShrink: 0,
          display: "flex",
          flexWrap: "wrap",
          gap: 4,
        }}
      >
        {LANGUAGES.map((l) => (
          <button
            key={l.code}
            onClick={() => onLanguageChange(l.code)}
            style={{
              padding: "2px 6px",
              borderRadius: 3,
              border: "1px solid " + (l.code === language ? "#7c3aed" : "#333"),
              background: l.code === language ? "#4c1d95" : "#1a1a2e",
              color: l.code === language ? "#e9d5ff" : "#888",
              fontSize: 10,
              cursor: "pointer",
              fontWeight: l.code === language ? 600 : 400,
            }}
          >
            {l.code === "automatic" ? "auto" : l.code}
          </button>
        ))}
      </div>

      {/* Error */}
      {error && (
        <div
          style={{
            padding: "4px 12px",
            fontSize: 11,
            color: "#f87171",
            background: "#1c0a0a",
            flexShrink: 0,
          }}
        >
          {error}
        </div>
      )}

      {exportStatus && (
        <div
          role="status"
          style={{
            padding: "4px 12px",
            fontSize: 10,
            color: exportStatus.includes("failed") ? "#f87171" : "#94a3b8",
            background: "#111827",
            flexShrink: 0,
          }}
        >
          {exportStatus}
        </div>
      )}

      {/* Transcript content — fills remaining space */}
      <div
        ref={scrollRef}
        style={{
          padding: "8px 12px",
          fontSize: 13,
          lineHeight: 1.6,
          color: "#e2e8f0",
          flex: 1,
          overflowY: "auto",
          scrollbarWidth: "thin",
          scrollbarColor: "#4b5563 #0d0d1a",
        }}
      >
        {transcripts.length === 0 && !currentPartial && !isListening && (
          <span style={{ color: "#555" }}>
            Click Start to begin live transcription...
          </span>
        )}
        {transcripts.length === 0 && !currentPartial && isListening && (
          <span style={{ color: "#555" }}>Listening...</span>
        )}

        {transcripts.map((t, i) => (
          <div key={i} style={{ marginBottom: 4 }}>
            <span>{t.text}</span>
            {t.language && (
              <span style={{ fontSize: 9, color: "#666", marginLeft: 4 }}>
                [{t.language}]
              </span>
            )}
          </div>
        ))}

        {currentPartial && (
          <div style={{ color: "#94a3b8", fontStyle: "italic" }}>
            {currentPartial}
          </div>
        )}
      </div>

      {/* Debug info */}
      {debugInfo && (
        <div
          style={{
            padding: "3px 12px",
            fontSize: 10,
            fontFamily: "monospace",
            color: "#f59e0b",
            background: "#1a1500",
            borderTop: "1px solid #332d00",
            flexShrink: 0,
          }}
        >
          {debugInfo}
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </div>
  );
}
